import { css, styled } from "../../styles";

/** antd's date panel is a fixed width; everything here is sized off it. */
const PANEL_WIDTH = 280;

/**
 * Review (EN-3907): the box was taller than it needed to be. The padding and the gap below the
 * input came down together; the panel's own vertical rhythm is trimmed in the popup rules below.
 * The width is untouched — it is pinned to the panel and shrinking it clips the Saturday column.
 */
const CONTAINER_PADDING = 10;

/**
 * EN-3907: wrapper the antd panel is portalled into (via `getPopupContainer`),
 * so the calendar renders inside the call site's popover instead of at the end
 * of `<body>`. Keeping it in the same subtree is what lets the existing
 * click-outside handlers on the OptionsBars keep working unchanged.
 *
 * It has to be at least a panel wide plus its own padding. The first cut reused
 * react-datepicker's 269px with 24px side padding, leaving 221px of content,
 * which clipped the Saturday column and the month-navigation arrows clean off.
 */

export const DateRangePickerContainer = styled("div", {
  backgroundColor: "$white",
  boxSizing: "border-box",

  // Open Sans on the container and everything inside it. The panel is portalled in here by
  // getPopupContainer, so this catches every antd element whatever classes it ends up with --
  // belt and braces over the per-element rules, which name antd's own class names and stop
  // working the moment antd renames one.
  "&, & *": {
    fontFamily: "$openSans !important",
  },

  width: PANEL_WIDTH + CONTAINER_PADDING * 2,
  padding: CONTAINER_PADDING,
  borderRadius: "$6",

  position: "relative",
});

export const dateRangePickerInputCss = css({
  width: "100%",
  height: "38px",

  fontFamily: "$openSans !important",
  // The "-" between the two dates and the placeholder are separate elements from the inputs,
  // so they keep antd's own size unless told otherwise and the row looks mismatched.
  fontSize: "$text-lg",
  border: "2px solid $gray-50 !important",
  boxShadow: "none !important",

  "&:hover": {
    borderColor: "var(--colors-portal-text-input-border-hover) !important",
  },

  "&.ant-picker-focused": {
    borderColor: "var(--colors-portal-text-input-border-focus) !important",
    boxShadow: "0 0 0 2px rgba(189,180,221, 0.1) !important",
  },

  // Open Sans on every descendant, not just the input. antd sets its own family on the inner
  // elements, so a rule on the root alone left parts of the control rendering in Arial.
  "&, & *": {
    fontFamily: "$openSans !important",
  },

  ".ant-picker-input > input": {
    // Review (EN-3907): the dates started at $text-sm (10px) and are now $text-lg (14px).
    fontSize: "$text-lg",
    fontFamily: "$openSans !important",
    textAlign: "center",
  },

  /**
   * EN-3907 (review): the bar underlining whichever end of the range is being edited, which Abe
   * still saw in antd blue after the panel was themed.
   *
   * The rule lived in `datePickerPanelCss` and so never matched anything: rc-picker's
   * `RangeSelector` renders `-active-bar` as a child of the picker root, alongside the two
   * inputs — it is part of the field, not of the popup that class is attached to. It has to be
   * here, on the root, which is the element `dateRangePickerInputCss` lands on.
   *
   * Same variable as a selected day, so the field and the calendar move together when a tenant
   * changes its palette.
   */
  ".ant-picker-active-bar": {
    background:
      "var(--colors-portal-date-range-day-selected-background) !important",
  },

  // The clear button. antd positions it against the input's right edge and leaves the glyph to
  // sit on the text baseline, so the circle read as hanging rather than centred.
  ".ant-picker-clear": {
    display: "flex !important",
    alignItems: "center",
    justifyContent: "center",

    top: "50% !important",
    transform: "translateY(-50%)",
    lineHeight: 0,
  },
});

export const dateRangePickerPopupCss = css({
  // On every descendant: antd's header, cells and buttons carry their own font-family, and a
  // rule on the root alone left those rendering in Arial.
  "&, & *": {
    fontFamily: "$openSans !important",
  },

  // Sits inside the call site's popover, directly under the input, which is
  // the layout the react-datepicker version had (fields above, calendar below).
  position: "static !important",
  // Review (EN-3907): was $6 (12px). Half the gap, same layout.
  paddingTop: "$3",

  ".ant-picker-panel-container": {
    boxShadow: "none !important",
    background: "transparent !important",
  },

  ".ant-picker-range-arrow": {
    display: "none !important",
  },

  // One calendar, not two. antd renders a range as two side-by-side months;
  // the portal's filter has always been a single month you page through, and
  // two panels do not fit the popover at any sane width.
  //
  // Hiding the second panel alone would strand the user: antd puts the "next"
  // arrows on the second panel's header and hides the first panel's copies, so
  // dropping the second leaves no way to move forward a month — which is
  // exactly how the user picks an end date later than the start. Both halves of
  // this rule are load-bearing.
  ".ant-picker-panels > .ant-picker-panel:last-child": {
    display: "none",
  },

  ".ant-picker-panels > .ant-picker-panel:first-child": {
    ".ant-picker-header-next-btn, .ant-picker-header-super-next-btn": {
      visibility: "visible !important",
    },
  },

  ".ant-picker-date-panel, .ant-picker-panel": {
    width: `${PANEL_WIDTH}px !important`,
  },

  // Review (EN-3907): the rest of the height. antd's date panel ships generous padding around
  // the header and the week rows; these trim it without touching cell hit areas, which stay at
  // their default size so the days are no harder to click.
  ".ant-picker-date-panel .ant-picker-body": {
    padding: "8px 10px !important",
  },

  ".ant-picker-header": {
    padding: "0 6px !important",
    borderBottom: "none !important",
  },

  ".ant-picker-content th": {
    height: "28px !important",
    lineHeight: "28px !important",
  },
});
